import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';

const TypingAnimation = ({ text, speed = 25, startDelay = 0, showCursor = true, onComplete }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const previousTextRef = useRef(text);
  const onCompleteCalledRef = useRef(false);

  // Memoize onComplete to prevent re-triggers
  const handleComplete = useCallback(() => {
    if (onComplete && !onCompleteCalledRef.current) {
      onCompleteCalledRef.current = true;
      onComplete();
    }
  }, [onComplete]);

  // Reset only when text actually changes
  useEffect(() => {
    if (previousTextRef.current !== text) {
      previousTextRef.current = text;
      onCompleteCalledRef.current = false;
      setDisplayedText('');
      setCurrentIndex(0);
      setIsComplete(false);
      setHasStarted(false);
    }
  }, [text]);

  useEffect(() => {
    if (!text) return;

    const startTimer = setTimeout(() => {
      setHasStarted(true);
    }, startDelay);

    return () => clearTimeout(startTimer);
  }, [text, startDelay]);

  useEffect(() => {
    if (!hasStarted || !text || isComplete) return;

    if (currentIndex < text.length) {
      const timer = setTimeout(() => {
        setDisplayedText(text.slice(0, currentIndex + 1));
        setCurrentIndex(prev => prev + 1);
      }, speed);

      return () => clearTimeout(timer);
    } else {
      setIsComplete(true);
      handleComplete();
    }
  }, [text, currentIndex, speed, hasStarted, isComplete, handleComplete]);

  return (
    <div className="typing-animation prose prose-sm max-w-none prose-headings:text-slate-800 prose-headings:font-semibold prose-p:text-slate-700 prose-p:leading-relaxed prose-strong:text-slate-900 prose-ul:text-slate-700 prose-li:text-slate-700">
      <ReactMarkdown
        components={{
          h2: ({ children }) => (
            <h2 className="text-xl font-semibold text-slate-800 mb-3 flex items-center">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-lg font-medium text-slate-800 mb-2 flex items-center">
              {children}
            </h3>
          ),
          p: ({ children }) => (
            <p className="text-slate-700 leading-relaxed mb-3">
              {children}
            </p>
          ),
          strong: ({ children }) => (
            <strong className="font-semibold text-slate-900">
              {children}
            </strong>
          ),
          ul: ({ children }) => (
            <ul className="list-disc list-inside space-y-1 text-slate-700 mb-3">
              {children}
            </ul>
          ),
          li: ({ children }) => (
            <li className="text-slate-700">
              {children}
            </li>
          ),
        }}
      >
        {displayedText}
      </ReactMarkdown>
      
      {showCursor && !isComplete && hasStarted && (
        <span className="inline-block w-0.5 h-4 bg-blue-600 ml-1 typing-cursor" />
      )}
      
      <style jsx>{`
        .typing-cursor {
          animation: smoothBlink 1.2s ease-in-out infinite;
        }
        
        @keyframes smoothBlink {
          0%, 45% { opacity: 1; }
          55%, 100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default TypingAnimation;
